import React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { VendorService } from '@/services/vendor.service';
import { PurchaseOrderStatus } from '@/models/vendor.model';
import { Button, Card, Badge, LoadingSpinner, EmptyState } from '@/components/common';
import { Package, ArrowLeft, Edit, CheckCircle2, Send, XCircle, DollarSign, Calendar, FileText } from 'lucide-react';

export const PurchaseOrderDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { profile } = useAuth();

  const { data: po, isLoading, error } = useQuery({
    queryKey: ['purchaseOrder', id],
    queryFn: () => (id ? VendorService.getPurchaseOrderById(id) : null),
    enabled: Boolean(id),
  });

  const updateMutation = useMutation({
    mutationFn: async (updates: { status: PurchaseOrderStatus; approvedBy?: string; approvedAt?: Date }) => {
      if (!id) return;
      await VendorService.updatePurchaseOrder(id, updates);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['purchaseOrder', id] });
      queryClient.invalidateQueries({ queryKey: ['purchaseOrders'] });
    },
  });

  const getStatusVariant = (status: PurchaseOrderStatus): "success" | "warning" | "danger" | "gray" | "info" => {
    const variants: Record<string, "success" | "warning" | "danger" | "gray" | "info"> = {
      [PurchaseOrderStatus.draft]: 'gray',
      [PurchaseOrderStatus.pending]: 'warning',
      [PurchaseOrderStatus.approved]: 'info',
      [PurchaseOrderStatus.sent]: 'info',
      [PurchaseOrderStatus.received]: 'success',
      [PurchaseOrderStatus.cancelled]: 'gray',
    };
    return variants[status] || 'gray';
  };

  if (isLoading) return <LoadingSpinner text="Loading purchase order..." fullScreen />;

  if (error || !po) {
    return (
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <EmptyState
          icon={Package}
          title={error ? "Failed to load purchase order" : "Purchase order not found"}
          description={error instanceof Error ? error.message : "The requested purchase order does not exist."}
          action={
            <Button variant="primary" icon={ArrowLeft} onClick={() => navigate('/vendors')}>
              Back to Vendors
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6">
        <Button
          variant="ghost"
          icon={ArrowLeft}
          onClick={() => navigate('/vendors')}
          className="mb-4"
        >
          Back to Vendors
        </Button>
        <Card>
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-4 flex-1">
              <div className="w-16 h-16 rounded-lg bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center">
                <Package className="w-8 h-8 text-blue-600" />
              </div>
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <h1 className="text-3xl font-bold text-gray-900">PO {po.poNumber || po.id}</h1>
                  <Badge variant={getStatusVariant(po.status)} size="md">
                    {po.status}
                  </Badge>
                </div>
                <div className="flex items-center gap-4 text-sm text-gray-600 flex-wrap">
                  <span>Vendor: <Link to={`/vendors/${po.vendorId}`} className="text-blue-600 hover:text-blue-800">{po.vendorName}</Link></span>
                  <span className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    {po.orderDate.toLocaleDateString()}
                  </span>
                </div>
              </div>
            </div>
            <Button
              variant="primary"
              size="sm"
              icon={Edit}
              onClick={() => navigate(`/vendors/purchase-orders/${po.id}/edit`)}
            >
              Edit
            </Button>
          </div>
        </Card>
      </div>

      <Card>
        <div className="flex flex-wrap items-center gap-3 mb-6 pb-6 border-b border-gray-200">
          {po.status === PurchaseOrderStatus.draft && (
            <Button
              variant="primary"
              size="sm"
              onClick={() => updateMutation.mutate({ status: PurchaseOrderStatus.pending })}
              isLoading={updateMutation.isPending}
            >
              Submit for approval
            </Button>
          )}
          {po.status === PurchaseOrderStatus.pending && (
            <Button
              variant="success"
              size="sm"
              icon={CheckCircle2}
              onClick={() =>
                updateMutation.mutate({
                  status: PurchaseOrderStatus.approved,
                  approvedBy: profile?.id || '',
                  approvedAt: new Date(),
                })
              }
              isLoading={updateMutation.isPending}
            >
              Approve
            </Button>
          )}
          {po.status === PurchaseOrderStatus.approved && (
            <Button
              variant="primary"
              size="sm"
              icon={Send}
              onClick={() => updateMutation.mutate({ status: PurchaseOrderStatus.sent })}
              isLoading={updateMutation.isPending}
            >
              Mark sent
            </Button>
          )}
          {po.status === PurchaseOrderStatus.sent && (
            <Button
              variant="success"
              size="sm"
              icon={CheckCircle2}
              onClick={() => updateMutation.mutate({ status: PurchaseOrderStatus.received })}
              isLoading={updateMutation.isPending}
            >
              Mark received
            </Button>
          )}
          {po.status !== PurchaseOrderStatus.cancelled && po.status !== PurchaseOrderStatus.received && (
            <Button
              variant="danger"
              size="sm"
              icon={XCircle}
              onClick={() => {
                const ok = window.confirm('Cancel this purchase order?');
                if (ok) updateMutation.mutate({ status: PurchaseOrderStatus.cancelled });
              }}
              isLoading={updateMutation.isPending}
            >
              Cancel
            </Button>
          )}
          {po.approvedAt && (
            <div className="text-sm text-gray-600 ml-auto flex items-center gap-1">
              <CheckCircle2 className="w-4 h-4" />
              Approved {po.approvedAt.toLocaleDateString()}
            </div>
          )}
        </div>

        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Description</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Category</th>
                <th className="px-6 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">Qty</th>
                <th className="px-6 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">Unit</th>
                <th className="px-6 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">Total</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {po.items.map((i) => (
                <tr key={i.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">{i.description}</td>
                  <td className="px-6 py-4 text-sm text-gray-600">{i.category || '—'}</td>
                  <td className="px-6 py-4 text-sm text-gray-900 text-right">{i.quantity}</td>
                  <td className="px-6 py-4 text-sm text-gray-900 text-right">${i.unitPrice.toLocaleString()}</td>
                  <td className="px-6 py-4 text-sm font-semibold text-gray-900 text-right">${i.total.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="bg-gradient-to-br from-gray-50 to-gray-100">
            <div className="text-sm font-medium text-gray-600 mb-1">Subtotal</div>
            <div className="text-2xl font-bold text-gray-900">${po.subtotal.toLocaleString()}</div>
          </Card>
          <Card className="bg-gradient-to-br from-gray-50 to-gray-100">
            <div className="text-sm font-medium text-gray-600 mb-1">Tax + Shipping</div>
            <div className="text-2xl font-bold text-gray-900">${(po.tax + po.shipping).toLocaleString()}</div>
          </Card>
          <Card className="bg-gradient-to-br from-blue-50 to-blue-100">
            <div className="text-sm font-medium text-blue-600 mb-1">Total</div>
            <div className="text-2xl font-bold text-blue-900">${po.total.toLocaleString()}</div>
          </Card>
        </div>

        {po.notes ? (
          <div className="mt-6">
            <div className="text-sm font-medium text-gray-600 mb-2 flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Notes
            </div>
            <div className="text-sm text-gray-900 whitespace-pre-wrap bg-gray-50 p-4 rounded-lg">{po.notes}</div>
          </div>
        ) : null}
      </Card>
    </div>
  );
};

